import React from "react";
import styled from "styled-components";
import { brand_orange_deep } from "../../constants/styles";
import PagedCarousel from "../Carousel";
import SectionShell, { SectionTitle, SectionSubtitle } from "../SectionShell";
import usePagedSection from "../usePagedSection";
import CardActionsOverlay from "../CardActionsOverlay";
import { Skeleton } from "../Skeleton";
import { apiGet } from "../../api";

const Card = styled.article`
  display: flex;
  flex-direction: column;
  gap: 10px;
  transition: transform 200ms ease;

  &:hover {
    transform: translateY(-4px);
  }
`;

const Thumbnail = styled.div`
  aspect-ratio: 16/10;
  border-radius: 12px;
  background:
    radial-gradient(
      ellipse at 30% 30%,
      rgba(255, 255, 255, 0.25),
      transparent 50%
    ),
    linear-gradient(135deg, #fbbf24, #f59e0b);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  position: relative;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, transparent 55%, rgba(0, 0, 0, 0.18));
    pointer-events: none;
  }
`;

const ThumbnailImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const CardTitle = styled.div`
  color: ${brand_orange_deep};
  font-weight: 700;
  font-size: 16px;
`;

const CardSubtitle = styled.div`
  color: #6b7280;
  font-size: 12px;
  line-height: 1.5;

  [data-theme="dark"] & {
    color: #d1d5db;
  }
`;

function renderTalkSkeleton(i) {
  return (
    <Card key={`sk-${i}`} aria-hidden="true">
      <Skeleton $radius="12px" style={{ aspectRatio: "16 / 10" }} />
      <Skeleton $width="55%" $height="16px" $radius="4px" />
      <Skeleton $width="85%" $height="12px" $radius="4px" />
    </Card>
  );
}

function renderTalkCard(t) {
  return (
    <Card key={t.id}>
      <Thumbnail data-card-host tabIndex={0}>
        {t.image_url && (
          <ThumbnailImage
            src={t.image_url}
            alt={t.title || ""}
            loading="lazy"
          />
        )}
        <CardActionsOverlay
          resourceType={t.resource_type || "Talk"}
          resourceId={t.resource_id || t.id}
          detailLink={t.detail_link}
          initialLiked={!!t.is_liked}
          initialDisliked={!!t.is_disliked}
          initialBookmarked={!!t.is_bookmarked}
        />
      </Thumbnail>
      <CardTitle>{t.title}</CardTitle>
      <CardSubtitle>{t.subtitle}</CardSubtitle>
    </Card>
  );
}

function TalksamtalkCarousel() {
  const loader = React.useCallback(async (pageIdx, perPage) => {
    const { parsedResponse } = await apiGet(
      `/api/v1/homepage/talks?page=${pageIdx + 1}&per_page=${perPage}`,
    );
    return {
      items: parsedResponse?.data ?? [],
      totalCount: parsedResponse?.meta?.total ?? 0,
    };
  }, []);

  const { items, pageIdx, setPageIdx, totalPages, loading, perPage } =
    usePagedSection({ loader });

  return (
    <SectionShell id="talksamtalk">
      <SectionTitle>Talksamtalk</SectionTitle>
      <SectionSubtitle>
        Simak Seluruh Video Talksamtalk Topiknya Untuk Mendapatkan Sertifikat
        yang Kamu Butuhkan!
      </SectionSubtitle>
      <PagedCarousel
        items={items}
        perPage={perPage}
        pageIdx={pageIdx}
        totalPages={totalPages}
        onPageChange={setPageIdx}
        loading={loading}
        renderItem={renderTalkCard}
        renderSkeleton={renderTalkSkeleton}
        ariaLabel="Talksamtalk"
        emptyMessage="Belum ada episode Talksamtalk yang tersedia."
      />
    </SectionShell>
  );
}

export default React.memo(TalksamtalkCarousel);
